import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
const STORAGE_KEY = 'boxing_coach_ready_countdown_target';
const ONE_DAY = 24 * 60 * 60 * 1000;
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + ONE_DAY;
  const storedTarget = window.localStorage.getItem(STORAGE_KEY);
  if (storedTarget) {
    const parsedTarget = Number.parseInt(storedTarget, 10);
    if (!Number.isNaN(parsedTarget) && parsedTarget > Date.now()) return parsedTarget;
  }
  const target = Date.now() + ONE_DAY;
  try {
    window.localStorage.setItem(STORAGE_KEY, target.toString());
  } catch {
    /* storage unavailable */
  }
  return target;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(ONE_DAY);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const difference = target - Date.now();
      if (difference <= 0) {
        const nextTarget = Date.now() + ONE_DAY;
        try {
          window.localStorage.setItem(STORAGE_KEY, nextTarget.toString());
        } catch {
          /* storage unavailable */
        }
        setTarget(nextTarget);
        setRemaining(ONE_DAY);
        return;
      }
      setRemaining(difference);
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = ONE_DAY - remaining;
  const progress = Math.min(90, 65 + elapsed / ONE_DAY * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="mt-10 max-w-3xl mx-auto bg-card border border-gold/30 rounded-2xl p-6 md:p-8" data-id="element-55" data-mp="2igh6f3mxcj9c">
      <div className="text-center" data-id="element-56" data-mp="2jc1cil4ej0du">
        <div className="flex items-center justify-center gap-2 text-gold font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-57" data-mp="2jazzwc7qm5u0">
          <AlarmClock size={20} aria-hidden="true" data-id="element-58" data-mp="2ecomxsm5bf7k" />
          <span data-id="element-59" data-mp="29e52jwn7abhb">Launch price ends in:</span>
        </div>
        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-60" data-mp="29fpy9pl940gr">
          <TimeBox value={pad(hours)} label="hrs" data-id="element-61" data-mp="24lu2mtsgmpor" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-62" data-mp="1znk8mmih1v4q">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-63" data-mp="1vmgt614c6141" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-64" data-mp="1vnryi5dfjgx9">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-65" data-mp="1qnndq35ho018" />
        </div>
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-mist mb-8" data-id="element-66" data-mp="1loappf2vsmx2">
          The 5 bonus guides are included at this launch price.
        </p>
        <div className="space-y-2" data-id="element-67" data-mp="160afxkibh0n">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-68" data-mp="65328gbh7qqo">
            <span className="text-steel" data-id="element-69" data-mp="b3wiggi5dvuj">Remaining launch spots</span>
            <span className="text-gold" data-id="element-70" data-mp="b2lfgiwj0s4h">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-canvas rounded-full overflow-hidden border border-line" data-id="element-71" data-mp="f58b6uwbix6y">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} className="h-full bg-gold rounded-full" data-id="element-72" data-mp="3emzjoa8i0fr8" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-steel font-medium" data-id="element-73" data-mp="3sdujkx10rmio">
            {Math.round(progress)}% of spots filled
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-canvas border border-line rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px]" data-id="element-74" data-mp="2gsf2jg5azazl">
      <div className="flex items-baseline justify-center gap-1" data-id="element-75" data-mp="2budjgv7vnbo4">
        <span className="text-4xl md:text-5xl font-display font-bold text-white tabular-nums" data-id="element-76" data-mp="2qhoxd3rj77re">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-steel" data-id="element-77" data-mp="2lp5js6sugcml">{label}</span>
      </div>
    </div>;
}